import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiCheck, FiClock, FiXCircle, FiRefreshCw } from "react-icons/fi";

import { getOrderStatus, getOrder, formatBRL } from "../services/billingApi";
import "./PagamentoRetornoPage.css";

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 3000;

/**
 * Tela de retorno do Checkout Pro (`back_urls`).
 *
 * O Mercado Pago devolve o usuário aqui logo após o pagamento, muitas vezes
 * antes do webhook chegar. Por isso consultamos o gateway em loop curto até o
 * status estabilizar — o usuário vê a confirmação sem precisar recarregar.
 */
const PagamentoRetornoPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const orderId = searchParams.get("order");
  const hintedStatus = searchParams.get("status");

  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const check = useCallback(async () => {
    if (!orderId) {
      setState("unknown");
      return;
    }

    try {
      const status = await getOrderStatus(orderId);

      if (status?.paid) {
        setState("approved");
        setOrder(await getOrder(orderId).catch(() => null));
        return;
      }

      if (["REJECTED", "CANCELED", "EXPIRED"].includes(status?.orderStatus)) {
        setState("failed");
        return;
      }

      setState("pending");
    } catch {
      setState("pending");
    }
  }, [orderId]);

  useEffect(() => {
    check();
  }, [check]);

  // Repete a consulta enquanto o status não for definitivo.
  useEffect(() => {
    if (state !== "pending" || attempts >= MAX_ATTEMPTS) return undefined;

    const timer = setTimeout(() => {
      setAttempts((current) => current + 1);
      check();
    }, POLL_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [state, attempts, check]);

  const views = {
    checking: {
      icon: <FiRefreshCw className="retorno__spin" />,
      tone: "neutral",
      title: "Confirmando seu pagamento...",
      text: "Só um instante, estamos falando com o Mercado Pago.",
    },
    approved: {
      icon: <FiCheck />,
      tone: "success",
      title: "Pagamento aprovado!",
      text: order
        ? `${order.description} — ${formatBRL(order.amountCents)}. Tudo já está liberado na sua conta.`
        : "Tudo já está liberado na sua conta.",
    },
    pending: {
      icon: <FiClock />,
      tone: "warning",
      title:
        attempts >= MAX_ATTEMPTS
          ? "Pagamento em processamento"
          : "Aguardando confirmação...",
      text:
        attempts >= MAX_ATTEMPTS
          ? "Assim que o Mercado Pago confirmar, liberamos automaticamente e avisamos por e-mail. Você pode acompanhar em Faturas."
          : "Alguns meios de pagamento levam alguns instantes para compensar.",
    },
    failed: {
      icon: <FiXCircle />,
      tone: "danger",
      title: "Pagamento não concluído",
      text: "O pagamento foi recusado ou cancelado. Você pode tentar de novo com outro método.",
    },
    unknown: {
      icon: <FiClock />,
      tone: "neutral",
      title: "Não identificamos o pedido",
      text: "Confira o histórico em Faturas para ver a situação dos seus pagamentos.",
    },
  };

  const view = views[state] || views.checking;
  const failedHint = hintedStatus === "failure" && state === "pending";

  return (
    <div className="retorno fade-in">
      <div className="retorno__card">
        <div className="retorno__icon" data-tone={failedHint ? "danger" : view.tone}>
          {view.icon}
        </div>

        <h2>{view.title}</h2>
        <p>{view.text}</p>

        <div className="retorno__actions">
          <button
            type="button"
            className="button button--primary"
            onClick={() => navigate("/assinatura")}
          >
            Ir para Assinatura
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => navigate("/faturas")}
          >
            Ver faturas
          </button>
        </div>

        {state === "pending" && attempts < MAX_ATTEMPTS && (
          <p className="retorno__hint">
            Verificando novamente em instantes ({attempts + 1}/{MAX_ATTEMPTS})
          </p>
        )}
      </div>
    </div>
  );
};

export default PagamentoRetornoPage;
